import { FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PaginationBar from '../components/PaginationBar';
import { Customer, fetchCustomers } from '../api/customers';
import { runSyncJob } from '../api/syncData';

const PAGE_LIMIT = 20;

const Crm: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  useEffect(() => {
    if ((location.state as { refreshed?: boolean } | null)?.refreshed) {
      setRefreshCounter((prev) => prev + 1);
      navigate('/crm', { replace: true });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchCustomers({ page, limit: PAGE_LIMIT, search: debouncedSearch || undefined })
      .then((response) => {
        if (!isMounted) {
          return;
        }
        setCustomers(response.data);
        setTotal(response.total);
        setTotalPages(response.totalPages);
      })
      .catch((err) => {
        if (!isMounted) {
          return;
        }
        setError(err instanceof Error ? err.message : 'Failed to load customers.');
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [page, debouncedSearch, refreshCounter]);

  const totalPagesMemo = useMemo(() => Math.max(1, totalPages), [totalPages]);

  const renderName = (customer: Customer) => {
    const name = `${customer.firstName ?? ''} ${customer.lastName ?? ''}`.trim();
    return name || '—';
  };

  const handleSync = async () => {
    if (syncing) {
      return;
    }

    setSyncing(true);
    setSyncMessage(null);
    setError(null);

    try {
      const result = await runSyncJob('customers');
      setSyncMessage(`Synced ${result.syncedCount} customers from ERP.`);
      setRefreshCounter((prev) => prev + 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sync customers from ERP.';
      setError(message);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[96rem] rounded-[28px] border border-slate-200/70 bg-white/95 p-8 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)]">

      <div className="flex flex-col gap-3 pb-0 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Customers</h1>
          <p className="text-sm text-slate-500">Manage customer basics. Detailed 360° views are coming soon.</p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? 'Syncing…' : 'Sync from ERP'}
          </button>

          <button
            type="button"
            className="rounded-md border border-slate-200 bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm"
            onClick={() => navigate('/crm/add')}
          >
            + Add
          </button>
        </div>
      </div>

      {syncMessage ? (
        <div className="mt-4 rounded-xl border border-success/40 bg-success/10 px-4 py-3 text-sm text-success">
          {syncMessage}
        </div>
      ) : null}

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search name, email, or mobile..."
          className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:w-72"
        />
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
        <table className="min-w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3 font-semibold">Name</th>
              <th className="px-5 py-3 font-semibold">Email</th>
              <th className="px-5 py-3 font-semibold">Mobile</th>
              <th className="px-5 py-3 font-semibold">ERP ID</th>
              <th className="px-5 py-3 font-semibold text-center">Vehicles</th>
              <th className="px-5 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={6}>
                  Loading customers...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td className="px-5 py-6 text-center text-danger" colSpan={6}>
                  {error}
                </td>
              </tr>
            ) : customers.length === 0 ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={6}>
                  No customers found.
                </td>
              </tr>
            ) : (
              customers.map((customer) => (
                <tr key={customer.id} className="even:bg-slate-50/60">
                  <td className="px-5 py-3 font-medium text-slate-700">{renderName(customer)}</td>
                  <td className="px-5 py-3 text-slate-500">{customer.email ?? '—'}</td>
                  <td className="px-5 py-3 text-slate-500">{customer.mobileNumber ?? '—'}</td>
                  <td className="px-5 py-3 text-slate-500">{customer.erpId ?? '—'}</td>
                  <td className="px-5 py-3 text-center text-slate-700">
                    {customer.vehiclesCount ?? 0}
                  </td>
                  <td className="px-5 py-3 text-right space-x-2">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary hover:text-primary"
                      onClick={() => navigate(`/crm/view/${customer.id}`)}
                    >
                      View
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary hover:text-primary"
                      onClick={() => navigate(`/crm/${customer.id}`)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PaginationBar
        page={page}
        totalPages={totalPagesMemo}
        onPrev={() => setPage((prev) => Math.max(1, prev - 1))}
        onNext={() => setPage((prev) => Math.min(totalPagesMemo, prev + 1))}
        onSelect={(num) => setPage(num)}
        disabled={loading}
        renderSummary={
          <span>
            Showing {(page - 1) * PAGE_LIMIT + Math.min(PAGE_LIMIT, customers.length)} of {total} customers
          </span>
        }
      />
    </div>
  );
};

export default Crm;
